import { Extension, Node as TiptapNode } from '@tiptap/core';
import type { NodeViewRendererProps } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import Image from '@tiptap/extension-image';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

export const QUOTED_REPLY_HEADER_STYLE = 'margin: 0 0 1em 0;';
export const QUOTED_REPLY_BLOCKQUOTE_STYLE =
  'margin: 0 0 0 1em; padding-left: 1em; border-left: 3px solid #d0d7de; white-space: normal;';

const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) =>
          chain()
            .setMark('textStyle', { fontSize })
            .run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain()
            .setMark('textStyle', { fontSize: null })
            .removeEmptyTextStyle()
            .run(),
    };
  },
});

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (fontSize: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
  }
}

export const SignatureBlock = TiptapNode.create({
  name: 'signatureBlock',
  group: 'block',
  atom: true,
  isolating: true,
  draggable: false,
  selectable: true,
  addAttributes() {
    return {
      signatureId: {
        default: null,
      },
      html: {
        default: '',
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-signature-id]',
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) {
            return null;
          }
          const encodedHtml = element.getAttribute('data-signature-html');
          let htmlContent = element.innerHTML;
          if (encodedHtml) {
            try {
              htmlContent = decodeURIComponent(encodedHtml);
            } catch (error) {
              console.warn('[ComposeDialog] Failed to decode signature HTML from attribute', error);
            }
          }
          return {
            signatureId: element.getAttribute('data-signature-id'),
            html: htmlContent,
          };
        },
      },
    ];
  },
  renderHTML({ node }) {
    const attrs: Record<string, string> = {
      class: 'signature-block not-prose overflow-x-auto',
    };
    if (node.attrs.signatureId) {
      attrs['data-signature-id'] = node.attrs.signatureId;
    }
    if (node.attrs.html) {
      attrs['data-signature-html'] = encodeURIComponent(node.attrs.html);
    }
    return ['div', attrs];
  },
  addNodeView() {
    return ({ node }: NodeViewRendererProps) => {
      const dom = document.createElement('div');
      dom.className = 'signature-block not-prose overflow-x-auto';
      dom.dataset.signatureId = node.attrs.signatureId ?? '';
      if (node.attrs.html) {
        dom.dataset.signatureHtml = encodeURIComponent(node.attrs.html);
      } else {
        delete dom.dataset.signatureHtml;
      }
      dom.innerHTML = node.attrs.html ?? '';

      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type !== node.type) {
            return false;
          }
          dom.dataset.signatureId = updatedNode.attrs.signatureId ?? '';
          if (updatedNode.attrs.html) {
            dom.dataset.signatureHtml = encodeURIComponent(updatedNode.attrs.html);
          } else {
            delete dom.dataset.signatureHtml;
          }
          dom.innerHTML = updatedNode.attrs.html ?? '';
          return true;
        },
      };
    };
  },
});

export const QuotedContentBlock = TiptapNode.create({
  name: 'quotedContentBlock',
  group: 'block',
  atom: true,
  isolating: true,
  draggable: false,
  selectable: true,
  addAttributes() {
    return {
      html: {
        default: '',
      },
      replyHeader: {
        default: '',
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-quoted-content]',
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) {
            return null;
          }
          const encodedHtml = element.getAttribute('data-quoted-html');
          const encodedHeader = element.getAttribute('data-reply-header');
          let htmlContent = '';
          let headerContent = '';

          if (encodedHtml) {
            try {
              htmlContent = decodeURIComponent(encodedHtml);
            } catch (error) {
              console.warn('[ComposeDialog] Failed to decode quoted HTML from attribute', error);
            }
          }

          if (encodedHeader) {
            try {
              headerContent = decodeURIComponent(encodedHeader);
            } catch (error) {
              console.warn('[ComposeDialog] Failed to decode reply header from attribute', error);
            }
          }

          return {
            html: htmlContent,
            replyHeader: headerContent,
          };
        },
      },
    ];
  },
  renderHTML({ node }) {
    const attrs: Record<string, string> = {
      class: 'quoted-content-block',
      'data-quoted-content': 'true',
    };
    if (node.attrs.html) {
      attrs['data-quoted-html'] = encodeURIComponent(node.attrs.html);
    }
    if (node.attrs.replyHeader) {
      attrs['data-reply-header'] = encodeURIComponent(node.attrs.replyHeader);
    }
    return ['div', attrs];
  },
  addNodeView() {
    return ({ node }: NodeViewRendererProps) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'quoted-content-block';
      wrapper.dataset.quotedContent = 'true';
      wrapper.setAttribute('contenteditable', 'false');

      if (node.attrs.html) {
        wrapper.dataset.quotedHtml = encodeURIComponent(node.attrs.html);
      } else {
        delete wrapper.dataset.quotedHtml;
      }

      if (node.attrs.replyHeader) {
        wrapper.dataset.replyHeader = encodeURIComponent(node.attrs.replyHeader);
      } else {
        delete wrapper.dataset.replyHeader;
      }

      const headerMarkup = node.attrs.replyHeader
        ? `<p data-reply-header-display="true" style="${QUOTED_REPLY_HEADER_STYLE}">${node.attrs.replyHeader}</p>`
        : '';
      const quotedMarkup = node.attrs.html
        ? `<blockquote data-quoted-body="true" style="${QUOTED_REPLY_BLOCKQUOTE_STYLE}">${node.attrs.html}</blockquote>`
        : '';
      wrapper.innerHTML = `${headerMarkup}${quotedMarkup}`;

      return {
        dom: wrapper,
        update: (updatedNode) => {
          if (updatedNode.type !== node.type) {
            return false;
          }

          if (updatedNode.attrs.html) {
            wrapper.dataset.quotedHtml = encodeURIComponent(updatedNode.attrs.html);
          } else {
            delete wrapper.dataset.quotedHtml;
          }

          if (updatedNode.attrs.replyHeader) {
            wrapper.dataset.replyHeader = encodeURIComponent(updatedNode.attrs.replyHeader);
          } else {
            delete wrapper.dataset.replyHeader;
          }

          const updatedHeaderMarkup = updatedNode.attrs.replyHeader
            ? `<p data-reply-header-display="true" style="${QUOTED_REPLY_HEADER_STYLE}">${updatedNode.attrs.replyHeader}</p>`
            : '';
          const updatedQuotedMarkup = updatedNode.attrs.html
            ? `<blockquote data-quoted-body="true" style="${QUOTED_REPLY_BLOCKQUOTE_STYLE}">${updatedNode.attrs.html}</blockquote>`
            : '';

          wrapper.innerHTML = `${updatedHeaderMarkup}${updatedQuotedMarkup}`;
          return true;
        },
      };
    };
  },
});

function createStyleAttributeConfig(attributeName: string) {
  return {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute(attributeName),
    renderHTML: (attributes: Record<string, string | null>) => {
      if (!(attributeName in attributes)) {
        return {};
      }
      const value = attributes[attributeName];
      if (!value) {
        return {};
      }
      return { [attributeName]: value };
    },
  };
}

const StyledTable = Table.extend({
  allowGapCursor: true,
  addAttributes() {
    const parent = (this as { parent?: () => Record<string, unknown> }).parent?.() ?? {};
    return {
      ...parent,
      class: {
        default: 'border border-border/40',
      },
      style: createStyleAttributeConfig('style'),
      width: createStyleAttributeConfig('width'),
      height: createStyleAttributeConfig('height'),
    };
  },
});

const StyledTableHeader = TableHeader.extend({
  addAttributes() {
    const parent = (this as { parent?: () => Record<string, unknown> }).parent?.() ?? {};
    return {
      ...parent,
      style: createStyleAttributeConfig('style'),
      align: createStyleAttributeConfig('align'),
      width: createStyleAttributeConfig('width'),
      height: createStyleAttributeConfig('height'),
      colspan: createStyleAttributeConfig('colspan'),
      rowspan: createStyleAttributeConfig('rowspan'),
    };
  },
});

const StyledTableCell = TableCell.extend({
  addAttributes() {
    const parent = (this as { parent?: () => Record<string, unknown> }).parent?.() ?? {};
    return {
      ...parent,
      style: createStyleAttributeConfig('style'),
      align: createStyleAttributeConfig('align'),
      valign: createStyleAttributeConfig('valign'),
      width: createStyleAttributeConfig('width'),
      height: createStyleAttributeConfig('height'),
      colspan: createStyleAttributeConfig('colspan'),
      rowspan: createStyleAttributeConfig('rowspan'),
    };
  },
});

export const composeExtensions = [
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: true,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: true,
    },
    codeBlock: {
      HTMLAttributes: {
        class: 'rounded-lg border border-border/60 bg-muted/30 p-4 font-mono text-sm',
      },
    },
    heading: {
      levels: [1, 2, 3, 4],
    },
    link: false,
    underline: false,
  }),
  Underline,
  TextStyle,
  Color.configure({ types: ['textStyle'] }),
  FontFamily.configure({ types: ['textStyle'] }),
  FontSize,
  Highlight.configure({
    multicolor: true,
    HTMLAttributes: {
      class: 'bg-warning-surface/70 text-foreground',
    },
  }),
  Link.configure({
    openOnClick: false,
    autolink: true,
    linkOnPaste: true,
    HTMLAttributes: {
      class: 'text-primary underline decoration-primary/50 underline-offset-4 font-medium',
    },
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
  Image.configure({
    inline: false,
    allowBase64: true,
    HTMLAttributes: {
      class: 'rounded-lg border border-border/60',
    },
  }),
  StyledTable.configure({
    resizable: false,
    allowTableNodeSelection: false,
    HTMLAttributes: {
      class: 'border-collapse',
    },
  }),
  TableRow,
  StyledTableHeader,
  StyledTableCell,
  SignatureBlock,
  QuotedContentBlock,
  Placeholder.configure({
    placeholder: 'Write your message...',
  }),
];

export type ComposeRecipientFieldKey = 'to' | 'cc' | 'bcc';
